import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { useNavigate } from 'react-router';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import type { FadeAnimationStyle } from '@rio-cloud/rio-uikit/Fade';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';

import { DEFAULT_LOCALE } from '../configuration/lang/lang';
import { AppContent } from './AppContent';
import { useAppSelector } from '../configuration/setup/hooks';
import { getDisplayMessages, getLocale } from '../configuration/lang/langSlice';
import { AppContext } from './AppContext';
import { ErrorBoundary } from '../components/ErrorBoundary';
import {
    BROADCAST_ROUTE,
    CHAT_DETAILS_ROUTE,
    DEFAULT_ROUTE,
    FEEDBACK_ROUTE,
    HELP_ROUTE,
    NEW_CHAT_ROUTE,
} from '../routes/Router';
import { useNavigationDeepLink } from '../hooks/useNavigationDeepLink';
import { useDeepLink } from '../hooks/useDeepLink';
import { channelSelected, chatVisibilityChanged } from '../features/chat/chatSlice';
import { gaPush, TRACKING_CATEGORIES } from '../configuration/setup/googleAnalytics';
import { useGetChatActivationQuery } from '../services/chatAdministrationApi';
import { useUserAccount } from '../configuration/login/loginSlice';
import { useFleetGroupFilterPersistence } from '../features/fleetGroupFilter/useFleetGroupFilterPersistence';

const withSource = (label: string, source: string) => `${label}${source ? ' ' : ''}${source}`;

const AppLayout = () => {
    const dispatch = useDispatch();
    const userLocale = useAppSelector(getLocale);
    const displayMessages = useAppSelector(getDisplayMessages);

    const sidebarRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    const [animationStyle, setAnimationStyle] = useState<FadeAnimationStyle>('page');
    const [currentLocation, setCurrentLocation] = useState<string>(DEFAULT_ROUTE);

    useNavigationDeepLink((newLocation: string) => setCurrentLocation(newLocation));

    useDeepLink();

    const appRef = useRef<HTMLDivElement>(null);
    const onScreen = useOnScreen(appRef);

    const accountId = useUserAccount();
    useGetChatActivationQuery({ accountId: accountId ?? 'missingId' });

    useFleetGroupFilterPersistence();

    useEffect(() => {
        dispatch(chatVisibilityChanged(onScreen));
    }, [onScreen]);

    if (!(displayMessages && userLocale)) {
        return null;
    }

    const handleNavigateToDefault = (source = '') => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            action: 'Navigate to default',
            label: withSource('Opened chat list page', source),
        });

        dispatch(channelSelected(undefined));
        navigate(DEFAULT_ROUTE);
        setCurrentLocation(DEFAULT_ROUTE);
        setAnimationStyle('pageBack');
    };

    const handleNavigateToNewChat = (source = '') => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            action: 'Navigate to new chat',
            label: withSource('Opened recipients page for new chat', source),
        });

        navigate(NEW_CHAT_ROUTE);
        setCurrentLocation(NEW_CHAT_ROUTE);
        setAnimationStyle('page');
    };

    const handleNavigateToBroadcast = (source = '') => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            action: 'Navigate to broadcast',
            label: withSource('Opened broadcast page', source),
        });

        navigate(BROADCAST_ROUTE);
        setCurrentLocation(BROADCAST_ROUTE);
        setAnimationStyle('page');
    };

    const handleNavigateToChat = (channelId: string, source = '') => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            action: 'Navigate to chat',
            label: withSource('Opened chat details page', source),
        });

        navigate(`${CHAT_DETAILS_ROUTE}?channelId=${channelId}`);
        setCurrentLocation(CHAT_DETAILS_ROUTE);
        setAnimationStyle('page');
    };

    const handleNavigateToFeedback = (source = '') => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            action: 'Navigate to feedback',
            label: withSource('Opened feedback page', source),
        });

        navigate(FEEDBACK_ROUTE);
        setCurrentLocation(FEEDBACK_ROUTE);
        setAnimationStyle('page');
    };

    const handleNavigateToHelp = (source = '') => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            action: 'Navigate to help',
            label: withSource('Opened help page', source),
        });

        navigate(HELP_ROUTE);
        setCurrentLocation(HELP_ROUTE);
        setAnimationStyle('page');
    };

    return (
        <ErrorBoundary>
            <IntlProvider
                defaultLocale={DEFAULT_LOCALE}
                key={userLocale}
                locale={userLocale}
                messages={displayMessages}
            >
                <AppContext.Provider
                    value={{
                        sidebarRef,
                        navigateToDefault: handleNavigateToDefault,
                        navigateToNewChat: handleNavigateToNewChat,
                        navigateToBroadcast: handleNavigateToBroadcast,
                        navigateToChat: handleNavigateToChat,
                        navigateToFeedback: handleNavigateToFeedback,
                        navigateToHelp: handleNavigateToHelp,
                    }}
                >
                    <ApplicationLayout
                        layoutRef={appRef}
                        className='bg-white max-width-800 margin-auto overflow-hidden height-100vh'
                        data-testid='app-layout'
                    >
                        <AppContent animationStyle={animationStyle} currentLocation={currentLocation} />
                    </ApplicationLayout>
                </AppContext.Provider>
            </IntlProvider>
        </ErrorBoundary>
    );
};

export default AppLayout;
